import CustomizeAgent from 'components/app/AIAgent/CustomizeAgent'
import HandoffRules from 'components/app/AIAgent/HandoffRules'
import KnowledgeBaseForm from 'components/app/AIAgent/KnowledgeBaseForm'
import KnowledgeBaseTable from 'components/app/AIAgent/KnowledgeBaseTable'
import PhoneRoutingRules from 'components/app/AIAgent/PhoneRoutingRules'
import PhoneRoutingRuleForm from 'components/app/AIAgent/PhoneRoutingRuleForm'
import { Divider, FlexRow } from 'components/lib'
import Tabs from 'components/lib/Navigation/Tabs'
import {
  getAssistantConfiguration,
  getKnowledgeBases,
} from 'api/assistant/knowledgeBase'
import React, { useEffect, useState, useMemo } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  allKnowledgeBasesAtom,
  assistantConfigAtom,
  authenticatedUserAtom,
  knowledgeBaseFormAtom,
  phoneRoutingRuleFormAtom,
} from 'state/atoms'
import { useTheme } from 'styled-components'
import { toastError } from 'components/lib/Toast'

const agentTabs = [
  {
    name: 'Knowledge',
  },
  {
    name: 'Handoff rules',
  },
  {
    name: 'Customize agent',
  },
  {
    name: 'Phone routing rules',
  },
]

const AIAgent = () => {
  const theme = useTheme()
  const [currentTab, setCurrentTab] = React.useState(0)
  const knowledgeBaseForm = useRecoilValue(knowledgeBaseFormAtom)
  const phoneRoutingRuleForm = useRecoilValue(phoneRoutingRuleFormAtom)
  const { selectedContact } = useRecoilValue(authenticatedUserAtom)
  const [, setKnowledgeBases] = useRecoilState(allKnowledgeBasesAtom)
  const [assistantConfig, setAssistantConfig] =
    useRecoilState(assistantConfigAtom)
  const [isKBLoading, setIsKBLoading] = useState(false)
  const [, setIsConfigLoading] = useState(false)

  // Check if phone routing is enabled for this company
  const isPhoneRoutingEnabled = useMemo(() => {
    const featureFlag = process.env.REACT_APP_PHONE_ROUTING_COMPANIES || ''
    const enabledCompanyIds = featureFlag.split(',').map(id => id.trim())
    return enabledCompanyIds.includes(selectedContact.companyId)
  }, [selectedContact.companyId])

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedContact?.companyId) return
      setIsKBLoading(true)
      setIsConfigLoading(true)
      try {
        const kbResponse = await getKnowledgeBases(selectedContact.companyId)
        setKnowledgeBases(kbResponse)
        setIsKBLoading(false)
      } catch (error) {
        toastError('Error fetching knowledge bases')
        setIsKBLoading(false)
      }

      try {
        const configResponse = await getAssistantConfiguration(
          selectedContact.companyId,
        )
        setAssistantConfig(configResponse)
        setIsConfigLoading(false)
      } catch (error) {
        toastError('Error fetching assistant information')
        setIsConfigLoading(false)
      }
    }

    fetchData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContact?.companyId])

  return (
    <div
      style={{ padding: 20, paddingTop: 0, marginLeft: 20, marginBottom: 50 }}
    >
      {!knowledgeBaseForm.isOpen && !phoneRoutingRuleForm.isOpen ? (
        <>
          <h2 style={{ marginTop: 10 }}>AI agent</h2>
          <p style={{ color: theme.colors.text250, marginBottom: 15 }}>
            Customize your Super AI with knowledge, instructions on how to
            handoff tasks, how it sounds, and what it is and is not allowed to
            answer.
          </p>

          <FlexRow justify="space-between">
            <Tabs
              tabs={isPhoneRoutingEnabled ? agentTabs : agentTabs.slice(0, 3)}
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
              customStyle="z-0"
            />
            <Divider style={{ marginTop: 45, marginBottom: 0 }} />
          </FlexRow>

          {currentTab === 0 && <KnowledgeBaseTable isLoading={isKBLoading} />}
          {currentTab === 1 && <HandoffRules />}
          {currentTab === 2 && (
            <CustomizeAgent initialValues={assistantConfig} />
          )}
          {currentTab === 3 && isPhoneRoutingEnabled && <PhoneRoutingRules />}
        </>
      ) : knowledgeBaseForm.isOpen ? (
        <KnowledgeBaseForm />
      ) : (
        <PhoneRoutingRuleForm />
      )}
    </div>
  )
}

export default AIAgent
